// SPDX-License-Identifier: Apache-2.0

// /dev/notes — kernel diagnostic bench (was /notes). Moved here on 2026-04-26 so
// the kernel benches stop masquerading as the product. The page itself is
// unchanged from the original Notes.tsx; the only addition is the DevBanner.
//
// kernels 1 + 2 of 5 from kernels/MIGRATION.md, chained on one command encoder.
// int8 cosine similarity, then per-workgroup bitonic top-K → CPU merge.

import { createSignal, Show, For } from "solid-js";
import {
  createDevice,
  uploadCorpus,
  setupSearch,
  runSearch,
  disposeSearchAll,
  setupCosineSim,
  runCosineSim,
  disposeCosineSim,
  peekSims,
  type SearchSetup,
  type SimsDiag,
} from "@tether/kernels";

type BenchResult = {
  nDocs: number;
  dim: number;
  k: number;
  setupMs: number;
  uploadMs: number;
  warmupMs: number;
  runMs: number;
  mergeMs: number;
  candidatesScanned: number;
  topK: { idx: number; sim: number }[];
  simRange: { min: number; max: number };
  caps: {
    maxStorageBufferBindingMB: number;
    maxBufferMB: number;
    shaderI8: boolean;
    shaderF16: boolean;
  };
};

const K = 10;

export default function DevNotes() {
  const [error, setError] = createSignal<string | null>(null);
  const [status, setStatus] = createSignal<string>("idle — pick a size to bench cosineSim → topK");
  const [bench, setBench] = createSignal<BenchResult | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [diag, setDiag] = createSignal<{ nDocs: number; sims: SimsDiag } | null>(null);

  async function runDiag(nDocs: number) {
    setError(null);
    setDiag(null);
    setBench(null);
    setBusy(true);
    let setup: Awaited<ReturnType<typeof setupCosineSim>> | null = null;
    try {
      const dim = 384;

      setStatus("[diag] requesting WebGPU device…");
      const { device } = await createDevice();

      setStatus(`[diag] setting up cosineSim @ ${nDocs.toLocaleString()}…`);
      setup = await setupCosineSim(device, nDocs, dim);

      setStatus("[diag] generating + uploading corpus…");
      const corpus = new Int8Array(nDocs * dim);
      for (let i = 0; i < corpus.length; i++) {
        corpus[i] = (Math.random() * 254 - 127) | 0;
      }
      const scales = new Float32Array(nDocs + 1);
      for (let i = 0; i < scales.length; i++) scales[i] = 1 / 127;
      uploadCorpus(setup, corpus, scales);
      await setup.device.queue.onSubmittedWorkDone();

      const query = new Int8Array(dim);
      for (let i = 0; i < dim; i++) query[i] = (Math.random() * 254 - 127) | 0;

      setStatus("[diag] running cosineSim alone…");
      await runCosineSim(setup, query, 1 / 127);

      setStatus("[diag] peeking simsBuf…");
      const sims = await peekSims(setup, 32);

      setDiag({ nDocs, sims });
      setStatus("done");
    } catch (e) {
      setError(String(e instanceof Error ? `${e.message}\n${e.stack ?? ""}` : e));
      setStatus("error");
    } finally {
      if (setup) disposeCosineSim(setup);
      setBusy(false);
    }
  }

  async function run(nDocs: number) {
    setError(null);
    setBench(null);
    setBusy(true);
    let setup: SearchSetup | null = null;
    try {
      const dim = 384;

      setStatus("requesting WebGPU device…");
      const { device, caps } = await createDevice();

      setStatus(`creating pipelines + ${formatBytes(nDocs * dim + nDocs * 4 + dim + 4)} of buffers…`);
      const t0 = performance.now();
      setup = await setupSearch(device, nDocs, dim, K);
      const t1 = performance.now();

      setStatus(`generating ${nDocs.toLocaleString()} random int8 vectors…`);
      const corpus = new Int8Array(nDocs * dim);
      for (let i = 0; i < corpus.length; i++) {
        corpus[i] = (Math.random() * 254 - 127) | 0;
      }
      const scales = new Float32Array(nDocs + 1);
      for (let i = 0; i < scales.length; i++) scales[i] = 1 / 127;

      setStatus("uploading corpus to GPU…");
      const t2 = performance.now();
      uploadCorpus(setup.cosineSim, corpus, scales);
      await setup.cosineSim.device.queue.onSubmittedWorkDone();
      const t3 = performance.now();

      setStatus("warmup pass…");
      const query = new Int8Array(dim);
      for (let i = 0; i < dim; i++) query[i] = (Math.random() * 254 - 127) | 0;
      const queryScale = 1 / 127;
      const warmup = await runSearch(setup, query, queryScale);

      setStatus(`timed search @ ${nDocs.toLocaleString()} docs…`);
      const result = await runSearch(setup, query, queryScale);

      setBench({
        nDocs,
        dim,
        k: K,
        setupMs: t1 - t0,
        uploadMs: t3 - t2,
        warmupMs: warmup.totalMs,
        runMs: result.totalMs,
        mergeMs: result.mergeMs,
        candidatesScanned: result.candidatesScanned,
        topK: result.topK,
        simRange: result.simRange,
        caps: {
          maxStorageBufferBindingMB: caps.maxStorageBufferBindingSize / 1024 / 1024,
          maxBufferMB: caps.maxBufferSize / 1024 / 1024,
          shaderI8: caps.shaderI8,
          shaderF16: caps.shaderF16,
        },
      });
      setStatus("done");
    } catch (e) {
      setError(String(e instanceof Error ? `${e.message}\n${e.stack ?? ""}` : e));
      setStatus("error");
    } finally {
      if (setup) disposeSearchAll(setup);
      setBusy(false);
    }
  }

  return (
    <>
      <div class="dev-banner">DEV — kernel diagnostic page — internal use</div>
      <div style={{ padding: "32px", "max-width": "880px", margin: "0 auto", "font-family": "system-ui, sans-serif" }}>
        <h1 style={{ margin: "0 0 8px 0", "font-size": "28px", "letter-spacing": "-0.02em" }}>cosineSim → topK · WGSL bench</h1>
        <p style={{ color: "#9a9ab0", "margin-top": 0 }}>
          kernels 1 + 2 of 5 from <code>kernels/MIGRATION.md</code>, chained on one command encoder.
          int8 cosine similarity, then per-workgroup bitonic top-{K} → CPU merge of partials.
        </p>

        <div style={{ display: "flex", gap: "8px", margin: "24px 0", "flex-wrap": "wrap" }}>
          <button onClick={() => run(10_000)} disabled={busy()} style={btn}>10k docs</button>
          <button onClick={() => run(100_000)} disabled={busy()} style={btn}>100k docs</button>
          <button onClick={() => run(500_000)} disabled={busy()} style={btn}>500k docs</button>
          <button onClick={() => run(1_000_000)} disabled={busy()} style={btn}>1M docs</button>
          <button onClick={() => runDiag(10_000)} disabled={busy()} style={btnDiag}>diag (cosineSim only @ 10k)</button>
          <button onClick={() => runDiag(1_000_000)} disabled={busy()} style={btnDiag}>diag (cosineSim only @ 1M)</button>
        </div>

        <div style={{ "font-size": "14px", color: "#d8d8e8" }}>
          status: <code style={code}>{status()}</code>
        </div>

        <Show when={error()}>
          <pre style={{
            color: "#fca5a5",
            padding: "16px",
            background: "rgba(252,165,165,0.08)",
            border: "1px solid rgba(252,165,165,0.3)",
            "border-radius": "8px",
            margin: "16px 0",
            "white-space": "pre-wrap",
            "font-size": "13px",
          }}>{error()}</pre>
        </Show>

        <Show when={diag()}>
          {(d) => (
            <div style={{
              "margin-top": "24px",
              padding: "16px 20px",
              background: "rgba(254,240,138,0.06)",
              border: "1px solid rgba(254,240,138,0.3)",
              "border-radius": "12px",
            }}>
              <h2 style={{ margin: "0 0 12px 0", "font-size": "18px", color: "#fde047" }}>
                cosineSim-only diag · {d().nDocs.toLocaleString()} docs
              </h2>
              <table style={{ "border-collapse": "collapse", "font-size": "13px" }}>
                <tbody>
                  <tr><td style={cell}>sample size</td><td style={cellNum}><code>{d().sims.count}</code></td></tr>
                  <tr><td style={cell}>min · mean · max</td><td style={cellNum}><code>
                    {d().sims.min.toFixed(6)} · {d().sims.mean.toFixed(6)} · {d().sims.max.toFixed(6)}
                  </code></td></tr>
                  <tr><td style={cell}>count zero / NaN</td><td style={cellNum}><code style={{ color: d().sims.countZero === d().sims.count ? "#fca5a5" : "#86efac" }}>
                    {d().sims.countZero} zero · {d().sims.countNaN} NaN
                  </code></td></tr>
                  <tr><td style={cell}>first 8 sims</td><td style={cellNum}><code style={{ "font-size": "11px" }}>
                    [{d().sims.firstN.map((v) => v.toFixed(4)).join(", ")}]
                  </code></td></tr>
                </tbody>
              </table>
              <p style={{ "margin-top": "12px", "font-size": "12px", color: "#9a9ab0" }}>
                expected: random int8 vectors → mean ≈ 0, range ~[-0.5, 0.5], 0 zero, 0 NaN.
                if all-zero: cosineSim kernel didn't write (validation error in dispatch).
              </p>
            </div>
          )}
        </Show>

        <Show when={bench()}>
          {(b) => (
            <div style={{
              "margin-top": "32px",
              padding: "20px",
              background: "rgba(167,139,250,0.06)",
              border: "1px solid rgba(167,139,250,0.2)",
              "border-radius": "12px",
            }}>
              <h2 style={{ margin: "0 0 16px 0", "font-size": "20px" }}>
                {b().nDocs.toLocaleString()} docs × {b().dim} dim · top-{b().k}
              </h2>
              <table style={{ "border-collapse": "collapse", "font-size": "14px" }}>
                <tbody>
                  <tr><td style={cell}>setup (pipelines + buffers)</td><td style={cellNum}><code>{b().setupMs.toFixed(2)} ms</code></td></tr>
                  <tr><td style={cell}>upload corpus to GPU</td><td style={cellNum}><code>{b().uploadMs.toFixed(2)} ms</code></td></tr>
                  <tr><td style={cell}>warmup search (cold)</td><td style={cellNum}><code>{b().warmupMs.toFixed(2)} ms</code></td></tr>
                  <tr><td style={cell}>search [GPU + readback + CPU merge]</td><td style={cellNum}><code style={{ color: "#86efac" }}>{b().runMs.toFixed(2)} ms</code></td></tr>
                  <tr><td style={cell}>↳ CPU merge of {b().candidatesScanned.toLocaleString()} partials</td><td style={cellNum}><code>{b().mergeMs.toFixed(2)} ms</code></td></tr>
                  <tr><td style={cell}>throughput</td><td style={cellNum}><code style={{ color: "#86efac" }}>
                    {(b().nDocs / b().runMs * 1000 / 1e6).toFixed(2)}M docs/s
                  </code></td></tr>
                  <tr><td style={cell}>sim range across partials</td><td style={cellNum}><code>
                    [{b().simRange.min.toFixed(4)}, {b().simRange.max.toFixed(4)}]
                  </code></td></tr>
                </tbody>
              </table>
              <div style={{ "margin-top": "16px", "font-size": "12px", color: "#6a6a80" }}>
                device · max storage binding {b().caps.maxStorageBufferBindingMB.toFixed(0)} MB ·
                max buffer {b().caps.maxBufferMB.toFixed(0)} MB ·
                shader-i8 {b().caps.shaderI8 ? "✓" : "✗"} ·
                shader-f16 {b().caps.shaderF16 ? "✓" : "✗"}
              </div>
              <h3 style={{ "margin-top": "24px", "font-size": "14px", color: "#9a9ab0", "font-weight": 500 }}>
                top {b().k} (from GPU bitonic, not CPU sort):
              </h3>
              <ul style={{ "padding-left": "20px", "font-size": "13px", color: "#d8d8e8" }}>
                <For each={b().topK}>
                  {(m) => <li><code>doc {m.idx.toLocaleString()}: sim = {m.sim.toFixed(6)}</code></li>}
                </For>
              </ul>
            </div>
          )}
        </Show>

        <p style={{ "margin-top": "32px", "font-size": "12px", color: "#6a6a80" }}>
          For 1M docs × 384 int8: 96 MB corpus stays GPU-resident. Repeat runs redispatch only —
          no re-upload. Random data → top-{K} sims should hover near zero (orthogonal vectors);
          a real e5-small corpus will show clear winners. Per Prometheus's plan, the WebGPU spec's
          implicit barrier between compute passes (§21.5) lets us chain cosineSim → topK on one
          command encoder, no readback between.
        </p>
      </div>
    </>
  );
}

function formatBytes(n: number): string {
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(0)} MB`;
  if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

const btn = {
  padding: "10px 18px",
  background: "rgba(167,139,250,0.12)",
  border: "1px solid rgba(167,139,250,0.4)",
  color: "#e8d8ff",
  "border-radius": "8px",
  cursor: "pointer",
  "font-size": "14px",
  "font-family": "inherit",
} as const;

const btnDiag = {
  ...btn,
  background: "rgba(254,240,138,0.08)",
  border: "1px solid rgba(254,240,138,0.4)",
  color: "#fde047",
} as const;

const code = {
  padding: "2px 6px",
  background: "rgba(255,255,255,0.06)",
  "border-radius": "4px",
  "font-family": "ui-monospace, SF Mono, monospace",
} as const;

const cell = { padding: "6px 16px 6px 0", color: "#9a9ab0" } as const;
const cellNum = { padding: "6px 0", "font-variant-numeric": "tabular-nums" } as const;
